/* eslint-disable camelcase */
import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import { Modal } from "../../common-components/modal";
import Typography from "../../common-components/Typography";
import CustomTooltip from "../../common-components/CustomTooltip";
import { font } from "../../constants/font";
import content from "../../constants/content";
import {
  APP_Z_INDICES,
  LFTS_SUPPORTED_URL_PLATFORMS,
  PLAN,
  PLAN_PRISING_RES,
  PORTAL_ID,
  formatPriceByLocale,
  SYMBOL,
} from "../../constants";
import AppConfig, { API, API_URL, LANDING_PAGE_URL, STATIC_PATH } from "../../constants/config";
import useUpgrade from "../../helper/hooks/useUpgrade";
import useApi from "../../helper/hooks/useApi";
import useNotify from "../../helper/hooks/useNotify";
import { getLocalizedPlanPricing } from "../../helper/getLocalizedPlanPricing";
import { getSavingsAmount } from "../../helper/getSavingsAmount";
import { isEnterPrise } from "../../helper/checkUser";
import { gpa } from "../../redux/actions/appUtils";
import openUrl from "../../helper/openUrl";
import { loadScript } from "../../helper/loadRazorpayScript";
import PaymentModal from "../payments/paymentModal";
import PaymentSuccess from "../payments/paymentSuccess";
import BackdropLoader from "../../common-components/BackdropLoader";
import vmTheme from "../../constants/theme";
import TRACKERS from "../../constants/trackers";
import trackerEvents from "../../helper/trackerEvents";
import { useShortsContext } from "./shortsContextApi";
import contextActionType from "./context-action-type";

const DiscountLabel = styled.span`
  background: transparent linear-gradient(287deg, #775afd 0%, #ac67fd 100%) 0%
    0% no-repeat padding-box;
  border-radius: 13px;
  padding: 4px 8px;
  color: #fff;
  font: ${font.normal_600_11};
  margin-left: 6px;

  @media (max-width: 767px) {
    padding: 3px 6px;
    font: ${font.normalMicro_11};
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px;
  padding: 0px 0 4px;

  @media (max-width: 767px) {
    padding: 12px 0 4px;
  }
`;

const BilledAmountPlaceholder = styled.span`
  display: block;
  font: ${font.normalMicro12};
  visibility: hidden;
`;

const Page = styled.div`
  background: ${(props) => props.theme.shortsPricingPageBg};
  color: ${(props) => props.theme.shortsPricingTextPrimary};
  min-height: 100%;
  width: 100%;
  padding: 48px 32px 56px;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    padding: 40px 24px 48px;
  }

  @media (max-width: 767px) {
    padding: 24px 16px 32px;
  }
`;

const ContentWrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-bottom: 28px;
  gap: 20px;
  width: fit-content;
  margin: 20px auto;

  @media (max-width: 767px) {
    margin-bottom: 20px;
    gap: 16px;
  }
`;

const BillingToggle = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 100%;
  background: ${(props) => props.theme.shortsPricingToggleBg};
  border: 1px solid ${(props) => props.theme.shortsPricingToggleBorder};
  border-radius: 999px;
  padding: 4px;
`;

const BillingOption = styled.button`
  border: none;
  background: ${(props) => (props.$active ? props.theme.shortsPricingToggleActiveBg : "transparent")};
  color: ${(props) => props.theme.shortsPricingTextPrimary};
  font: ${(props) => (props.$active ? font.mediumMini : font.normalMini)};
  padding: 8px 20px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: ${(props) => (props.$active ? "0 1px 4px rgba(0, 0, 0, 0.08)" : "none")};
  transition: background 0.2s ease, box-shadow 0.2s ease;

  @media (max-width: 767px) {
    padding: 8px 14px;
    font: ${font.normalMicro12};
  }
`;

const PlanGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  gap: 20px;

  @media (max-width: 767px) {
    gap: 16px;
  }
`;

const PlanCard = styled.div`
  position: relative;
  border: 1px solid
    ${(props) => (props.$popular
    ? props.theme.shortsPricingTeamCardBorder
    : props.theme.shortsPricingCardBorder)};
  border-radius: 16px;
  padding: 24px;
  background: ${(props) => props.theme.shortsPricingCardBg};
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 360px;
  min-height: 854px;
  flex: 0 1 calc(33.333% - 7px);
  box-shadow: ${(props) => (props.$popular
    ? props.theme.shortsPricingPopularCardShadow
    : props.theme.shortsPricingCardShadow)};

  @media (max-width: 900px) {
    flex: 0 1 calc(50% - 10px);
    max-width: 400px;
  }

  @media (max-width: 767px) {
    padding: 20px 16px;
    border-radius: 12px;
    flex: 0 1 100%;
  }
`;

const PlanCardIntro = styled.div`
  min-height: 88px;

  @media (max-width: 767px) {
    min-height: auto;
  }
`;

const PlanTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PlanIcon = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: block;
`;

const PlanPriceSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  @media (max-width: 767px) {
    min-height: auto;
  }
`;

const PlanButtonRow = styled.div`
  flex-shrink: 0;
  margin-top: 20px;

  @media (max-width: 767px) {
    margin-top: 16px;
  }
`;

const PlanCardFeatures = styled.div`
  flex: 1;
  min-height: 0;
  margin-top: 20px;

  @media (max-width: 767px) {
    margin-top: 16px;
  }
`;

const PopularBadge = styled.span`
  position: absolute;
  top: 16px;
  right: 16px;
  background: ${(props) => props.theme.shortsPricingPopularBadgeBg};
  color: #ffffff;
  font: ${font.mediumMicro_10};
  padding: 4px 10px;
  border-radius: 999px;

  @media (max-width: 767px) {
    top: 12px;
    right: 12px;
    padding: 3px 8px;
    font: ${font.normalMicro_11};
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    font: ${font.normal_14_23};
    color: ${(props) => props.theme.shortsPricingTextSecondary};
    margin-bottom: 12px;
  }

  li::before {
    content: "";
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    background: url("${STATIC_PATH}green-tick.svg") no-repeat center / contain;
  }

  @media (max-width: 767px) {
    li {
      font: ${font.normalBase_21};
      margin-bottom: 10px;
    }
  }
`;

const CreditsNoteRow = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 4px;
  min-height: 40px;
  padding: 8px 0 0;
  font: ${font.normalMicro12};
  color: ${(props) => props.theme.shortsPricingTextSecondary};

  @media (max-width: 767px) {
    min-height: auto;
  }
`;

const FeatureLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const InfoIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 0;
  flex-shrink: 0;

  img {
    width: 14px;
    height: 14px;
    display: block;
    top: 4px;
    position: relative;
  }
`;

const UrlPlatformsPopover = styled.div`
  position: absolute;
  transform: translate(-50%, calc(-100% - 8px));
  background: rgba(0, 0, 0, 0.9);
  color: #fff;
  padding: 12px 14px;
  border-radius: 8px;
  z-index: ${APP_Z_INDICES.customtooltip.default};
  width: min(380px, calc(100vw - 32px));
  box-sizing: border-box;
  border: 1px solid #fff;
`;

const PlatformTitle = styled.p`
  margin: 0;
  font: ${font.mediumMicro_10};
  color: #fff;
`;

const PlatformGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px 8px;
  margin-top: 10px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PlatformItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font: ${font.normalMicro_11};
  color: #fff;

  img {
    width: 18px;
    height: 18px;
    object-fit: contain;
    flex-shrink: 0;
  }
`;

function PricingInfoIcon({ tooltipId, contentKey }) {
  return (
    <CustomTooltip
      tooltipId={tooltipId}
      dataTooltip={contentKey}
      tooltipPosition="top"
      font={font.normal_medium_12}
      delayVisibilityBy={0}
      width="220px"
      height="auto"
    >
      <InfoIconButton type="button" data-tooltip-id={tooltipId} aria-label="More info">
        <img src={`${STATIC_PATH}info-outline.svg`} alt="" />
      </InfoIconButton>
    </CustomTooltip>
  );
}

PricingInfoIcon.propTypes = {
  tooltipId: PropTypes.string.isRequired,
  contentKey: PropTypes.string.isRequired,
};

const UrlPlatformsInfoIcon = () => {
  const intl = useIntl();
  const triggerRef = useRef(null);
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const platforms = LFTS_SUPPORTED_URL_PLATFORMS.filter((platform) => platform.id !== "title");
  const title = intl.formatMessage({ id: content.SHORTS_PRICING_URL_PLATFORMS_TITLE });
  const portalDOM = document.getElementById(PORTAL_ID);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({
      top: rect.top + window.scrollY,
      left: rect.left + rect.width / 2,
    });
  }, []);

  const showPopover = useCallback(() => {
    updatePosition();
    setShow(true);
  }, [updatePosition]);

  const hidePopover = useCallback(() => {
    setShow(false);
  }, []);

  return (
    <>
      <InfoIconButton
        ref={triggerRef}
        type="button"
        onMouseEnter={showPopover}
        onMouseLeave={hidePopover}
        onFocus={showPopover}
        onBlur={hidePopover}
        aria-label={title}
      >
        <img src={`${STATIC_PATH}info-outline.svg`} alt="" style={{ top: "0px" }} />
      </InfoIconButton>
      {show && portalDOM && ReactDOM.createPortal(
        <UrlPlatformsPopover
          style={{ top: `${position.top}px`, left: `${position.left}px` }}
          onMouseEnter={showPopover}
          onMouseLeave={hidePopover}
        >
          <PlatformTitle>{title}</PlatformTitle>
          <PlatformGrid>
            {platforms.map((platform) => (
              <PlatformItem key={platform.id}>
                {platform.icon && (
                  <img src={`${STATIC_PATH}${platform.icon}`} alt="" />
                )}
                <span>{platform.label}</span>
              </PlatformItem>
            ))}
          </PlatformGrid>
        </UrlPlatformsPopover>,
        portalDOM,
      )}
    </>
  );
};

const getFeatureKey = (feature) => (typeof feature === "string" ? feature : feature.key);
const getFeatureInfoType = (feature) => (typeof feature === "object" ? feature.infoType : null);
const getFeatureComingSoon = (feature) => (typeof feature === "object" ? feature.comingSoon : false);

const ComingSoonText = styled.span`
  color: ${(props) => props.theme.shortsComingSoonColor};
`;

const FeaturesHeader = styled.p`
  font: ${font.mediumBase_21};
  color: ${(props) => props.theme.shortsPricingTextPrimary};
  margin: 0 0 12px;

  @media (max-width: 767px) {
    font: ${font.mediumMini};
    margin-bottom: 8px;
  }
`;

const PlanButton = styled.button`
  width: 100%;
  border: none;
  border-radius: 999px;
  padding: 12px 16px;
  font: ${font.mediumBase_21};
  cursor: pointer;
  white-space: nowrap;
  background: ${(props) => (props.$plan === PLAN.TEAM
    ? props.theme.shortsPricingTeamBtnBg
    : props.theme.shortsPricingDefaultBtnBg)};
  color: ${(props) => (props.$plan === PLAN.TEAM
    ? props.theme.shortsPricingTeamBtnColor
    : props.theme.shortsPricingDefaultBtnColor)};

  &:hover {
    opacity: 0.92;
  }

  @media (max-width: 767px) {
    padding: 12px 14px;
    font: ${font.mediumMini};
  }
`;

const SHORTS_PLANS = [
  {
    plan: PLAN.STARTER,
    icon: "starter-icon.svg",
    name: content.SHORTS_PRICING_STARTER_NAME,
    titleSubtext: content.SHORTS_PRICING_STARTER_SUBTEXT,
    creditsNote: content.SHORTS_PRICING_STARTER_CREDITS,
    creditsInfoTooltip: content.SHORTS_PRICING_STARTER_CREDITS_INFO,
    cta: content.SHORTS_PRICING_UPGRADE_STARTER,
    features: [
      content.SHORTS_PRICING_STARTER_FEATURE_CLIPPING,
      content.SHORTS_PRICING_STARTER_FEATURE_BROLLS,
      content.SHORTS_PRICING_STARTER_FEATURE_CAPTIONS,
      content.SHORTS_PRICING_STARTER_FEATURE_REFRAME,
      content.SHORTS_PRICING_STARTER_FEATURE_EXPORTS,
      { key: content.SHORTS_PRICING_STARTER_FEATURE_UPLOAD, infoType: "urlPlatforms" },
      content.SHORTS_PRICING_STARTER_FEATURE_SUITE,
      content.SHORTS_PRICING_STARTER_FEATURE_WORKSPACE,
      content.SHORTS_PRICING_STARTER_FEATURE_SUPPORT,
      { key: content.SHORTS_PRICING_STARTER_FEATURE_TEMPLATES, comingSoon: true },
    ],
  },
  {
    plan: PLAN.TEAM,
    icon: "teams-icon.svg",
    name: content.SHORTS_PRICING_TEAM_NAME,
    popular: true,
    titleSubtext: content.SHORTS_PRICING_TEAM_SUBTEXT,
    creditsNote: content.SHORTS_PRICING_TEAM_CREDITS,
    creditsInfoTooltip: content.SHORTS_PRICING_TEAM_CREDITS_INFO,
    cta: content.SHORTS_PRICING_UPGRADE_TEAM,
    featuresHeader: content.SHORTS_PRICING_TEAM_FEATURES_HEADER,
    features: [
      content.SHORTS_PRICING_TEAM_FEATURE_DUBBING,
      content.SHORTS_PRICING_TEAM_FEATURE_PROCESSING,
      content.SHORTS_PRICING_TEAM_FEATURE_SUPPORT,
      content.SHORTS_PRICING_TEAM_FEATURE_ASPECT_RATIO,
      content.SHORTS_PRICING_TEAM_FEATURE_COLLABORATION,
      content.SHORTS_PRICING_TEAM_FEATURE_EXTRA_SEATS,
      content.SHORTS_PRICING_TEAM_FEATURE_SUITE_ADVANCED,
      content.SHORTS_PRICING_TEAM_FEATURE_PUBLISH_SOCIALS,
      content.SHORTS_PRICING_TEAM_FEATURE_TRANSCRIPT,
      content.SHORTS_PRICING_TEAM_FEATURE_WEBINARS,
      { key: content.SHORTS_PRICING_TEAM_FEATURE_API },
      { key: content.SHORTS_PRICING_TEAM_FEATURE_CAPTIONS },
      { key: content.SHORTS_PRICING_TEAM_FEATURE_VOICE, comingSoon: true },
      { key: content.SHORTS_PRICING_TEAM_FEATURE_PRESETS, comingSoon: true },
    ],
  },
  {
    plan: PLAN.ENTERPRISE,
    name: content.SHORTS_PRICING_ENTERPRISE_NAME,
    titleSubtext: content.SHORTS_PRICING_ENTERPRISE_SUBTEXT,
    priceLabel: content.SHORTS_PRICING_ENTERPRISE_PRICE,
    creditsNote: content.SHORTS_PRICING_ENTERPRISE_CREDITS,
    cta: content.SHORTS_PRICING_CONTACT_SALES,
    featuresHeader: content.SHORTS_PRICING_ENTERPRISE_FEATURES_HEADER,
    features: [
      content.SHORTS_PRICING_ENTERPRISE_FEATURE_SEATS,
      content.SHORTS_PRICING_ENTERPRISE_FEATURE_SSO,
      content.SHORTS_PRICING_ENTERPRISE_FEATURE_SECURITY,
      content.SHORTS_PRICING_ENTERPRISE_FEATURE_SUCCESS,
      content.SHORTS_PRICING_ENTERPRISE_FEATURE_SLA,
      content.SHORTS_PRICING_ENTERPRISE_FEATURE_WORKFLOWS,
      content.SHORTS_PRICING_ENTERPRISE_FEATURE_ONBOARDING,
      content.SHORTS_PRICING_ENTERPRISE_FEATURE_CREDITS,
      content.SHORTS_PRICING_ENTERPRISE_FEATURE_PROCUREMENT,
    ],
  },
];

const ShortsPricingModal = () => {
  const intl = useIntl();
  const { get, post } = useApi();
  const { warn } = useNotify();
  const { hidePricingModal } = useUpgrade();
  const shortsContext = useShortsContext();
  const theme = useSelector((state) => state.app.get("theme"));
  const {
    userId,
    currentTeamId,
    username,
    email,
    plan: current_plan,
    projectId,
  } = useSelector((state) => state.userDetails);
  const appReducer = useSelector((state) => state.app);
  const ipCountryCode = appReducer.get("ipConfig").country_code;
  const countryName = appReducer.get("ipConfig").country_name;
  const contextDetails = useSelector((state) => state.app.get("planDetails"));
  const paymentMode = contextDetails?.payment_mode;
  const { member_count, current_cycle } = contextDetails || {};
  const { show, source, type, shortId } = useSelector((state) => state.app.get("showPricing").toJS());
  const isShortsPricingTrigger = show && (type === "longtoshort" || type !== "header" && type !== "reframe" && type !== "reframeApply") && window.location.pathname.includes("/shorts/");
  // hidePricingModal() clears Redux source before payment finishes, so keep the opened source.
  const upgradeSourceRef = useRef({ source: "", shortId: null });

  const [activeTab, setActiveTab] = useState(1);
  const [planCost, setPlanCost] = useState(PLAN_PRISING_RES);
  const [countryCode, setCountryCode] = useState("US");
  const [allPlanCurrency, setAllPlanCurrency] = useState(PLAN_PRISING_RES);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentSuccessModal, setPaymentSuccessModal] = useState(false);
  const [billingCycle, setBillingCycle] = useState("");
  const [monthlyAmount, setMonthlyAmount] = useState(0);
  const [yearlyAmount, setYearlyAmount] = useState(0);
  const [selectPlan, setSelectPlan] = useState("");
  const [paymentCycle, setPaymentCycle] = useState("yearly");
  const [showLoader, setShowLoader] = useState(false);
  const [regularPrice, setRegularPrice] = useState({});
  const [refundAmount, setRefundAmount] = useState(0);
  const [paymentCurrency, setPaymentCurrency] = useState(null);
  const [cummulativePrice, setCummulativePrice] = useState(0);
  const [totalPayableAmount, setTotalPayableAmount] = useState(0);
  const [disableLoading, setDisableLoading] = useState(false);
  const [save, setSave] = useState(25);
  const [paymentDetails, setPaymentDetails] = useState({
    price: 0,
    maskedcc: 0,
    brand: "",
    binData: "",
    oldNonce: "",
  });

  const trackLockedUpgradeSuccess = useCallback((paymentMethod, planName) => {
    const { source: upgradeSource, shortId: upgradeShortId } = upgradeSourceRef.current;
    if (upgradeSource !== TRACKERS.LFTS_LOCKED_UPGRADE_CLICK) return;
    trackerEvents(TRACKERS.LFTS_REFRAME_LITE_UPGRADE_LOCKED_SUCCESS, {
      projectId,
      ...(upgradeShortId ? { shortId: upgradeShortId } : {}),
      source: upgradeSource,
      plan: planName || selectPlan,
      paymentMethod,
      isBoth: 1,
    });
  }, [projectId, selectPlan]);

  useEffect(() => {
    if (show) {
      setActiveTab(1);
      upgradeSourceRef.current = {
        source: source || "",
        shortId: shortId || null,
      };
    }
  }, [show, source, shortId]);

  useEffect(() => {
    // Same as priceModal: razorpay users always get IN (₹) after card payment
    if (!ipCountryCode) return;
    
    get(`${API_URL}${AppConfig.API.PLAN_PRISING}`, {}, false).then((res) => {
      const code = (paymentMode === "bt" || paymentMode === "paypal") ? "US" : ipCountryCode;
      const localizedPlanCost = getLocalizedPlanPricing(res.data, code);
      setPlanCost(localizedPlanCost);
      setCountryCode(code);
      setAllPlanCurrency(res.data);
      setRegularPrice(localizedPlanCost || {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ipCountryCode, paymentMode]);

  const getSavePlanForTab = (tab) => (tab === 0 ? PLAN.STARTER : PLAN.TEAM);

  const updateSaveFromTab = useCallback((tab) => {
    const planName = getSavePlanForTab(tab);
    const month = planCost[planName]?.monthly;
    const yearly = planCost[planName]?.yearly;
    if (month && yearly) {
      setSave(Math.round(getSavingsAmount(month, yearly)));
    }
  }, [planCost]);

  useEffect(() => {
    updateSaveFromTab(activeTab);
  }, [planCost, activeTab, updateSaveFromTab]);

  useEffect(() => {
    const planCycle = activeTab === 0 ? "monthly" : "yearly";
    setPaymentCycle(planCycle);
    setBillingCycle(planCycle);
  }, [activeTab]);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  const handleClose = useCallback(() => {
    hidePricingModal();
    if (shortsContext?.setShortsContext) {
      shortsContext.setShortsContext(contextActionType.CLOSE_SHORT_PRESET);
    }
  }, [hidePricingModal, shortsContext]);

  const handleContentWrapClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  useEffect(() => {
    if (!paymentSuccessModal) return undefined;
    const timer = setTimeout(() => {
      hidePricingModal();
      window.location.reload();
    }, 2000);
    return () => clearTimeout(timer);
  }, [paymentSuccessModal, hidePricingModal]);

  const getPlansPricingDetails = useCallback((planName) => {
    if (!currentTeamId) {
      warn(content.SOMETHING_WENT_WRONG);
      return;
    }

    setShowLoader(true);
    const proration = current_plan !== PLAN.LITE;
    const teamReq = {
      a: window.btoa(currentTeamId),
      b: window.btoa("upgrade"),
      c: window.btoa(String(proration)),
      d: window.btoa(planName),
    };

    gpa(teamReq).then((res) => {
      if (!res.error) {
        const decodePrice =
          window.atob(res.g) === "yearly"
            ? window.atob(res.f)
            : window.atob(res.e);
        setYearlyAmount(Number(parseFloat(window.atob(res.f)).toFixed(2)));
        setMonthlyAmount(Number(parseFloat(window.atob(res.e)).toFixed(2)));
        setRefundAmount(parseFloat(window.atob(res.r)).toFixed(2));
        setPaymentDetails({
          price: parseFloat(decodePrice),
          maskedcc: parseFloat(window.atob(res.a)),
          brand: window.atob(res.b),
          binData: window.atob(res.bd),
          oldNonce: window.atob(res.n),
        });
        setShowLoader(false);
        setShowPaymentModal(true);
      } else {
        setShowLoader(false);
        warn(res.errormsg || res.msg || content.SOMETHING_WENT_WRONG);
      }
    }).catch(() => {
      setShowLoader(false);
      warn(content.SOMETHING_WENT_WRONG);
    });
  }, [currentTeamId, current_plan, warn]);

  const upgradeHandler = useCallback((e, planName, tab, monthlyPrice) => {
    e && e.preventDefault() && e.stopPropagation();
    const planCycle = tab === 0 ? "monthly" : "yearly";
    setPaymentCycle(planCycle);
    setBillingCycle(planCycle);
    setSelectPlan(planName);

    if (planCycle === "yearly") {
      setTotalPayableAmount((monthlyPrice * 12) * member_count);
      setCummulativePrice(monthlyPrice * 12);
    } else {
      setTotalPayableAmount(monthlyPrice * member_count);
      setCummulativePrice(monthlyPrice);
    }
  }, [member_count]);

  const contactUsHandler = useCallback(() => {
    const queryParams = new URLSearchParams();
    if (email) queryParams.append("email", email);
    if (username) queryParams.append("name", username);
    if (userId) queryParams.append("userId", userId);
    queryParams.append("source", source || "Shorts-pricing");

    hidePricingModal();
    if (shortsContext?.setShortsContext) {
      shortsContext.setShortsContext(contextActionType.CLOSE_SHORT_PRESET);
    }
    setTimeout(() => {
      openUrl(`${LANDING_PAGE_URL}/book-a-demo?${queryParams.toString()}`, false);
    }, 500);
  }, [email, hidePricingModal, source, userId, username, shortsContext]);

  const handlePlanClick = useCallback((e, planName, tab) => {
    if (isEnterPrise(planName)) {
      contactUsHandler();
      return;
    }

    if (
      planName === PLAN.STARTER
      && current_plan === PLAN.STARTER
      && current_cycle === "monthly"
      && tab === 0
    ) {
      warn("Please select plan");
      return;
    }

    const monthlyPrice = tab === 0
      ? planCost[planName]?.monthly ?? 0
      : (planCost[planName]?.yearly ?? 0) / 12;

    hidePricingModal();
    upgradeHandler(e, planName, tab, monthlyPrice);
    getPlansPricingDetails(planName);
  }, [
    contactUsHandler,
    current_cycle,
    current_plan,
    getPlansPricingDetails,
    hidePricingModal,
    planCost,
    upgradeHandler,
    warn,
  ]);

  const handleOnClose = useCallback(() => {
    setShowPaymentModal(false);
    if (shortsContext?.setShortsContext) {
      shortsContext.setShortsContext(contextActionType.CLOSE_SHORT_PRESET);
    }
  }, [shortsContext]);

  const handlePaymentCurrency = useCallback((currency) => {
    const staticData = currency === "USD" ? currency : "IN";
    setPaymentCurrency(staticData);
    setRegularPrice(getLocalizedPlanPricing(allPlanCurrency, staticData));
  }, [allPlanCurrency]);

  const handleRazorpaySuccess = useCallback((payload, url) => {
    post(`${url}`, payload, true).then((res) => {
      setDisableLoading(true);
      setShowPaymentModal(false);
      setShowLoader(false);
      if (!res.error) {
        trackLockedUpgradeSuccess("razorpay", selectPlan);
        setPaymentSuccessModal(true);
      }
    }).catch(() => {
      setDisableLoading(true);
      setShowPaymentModal(false);
      setShowLoader(false);
    });
  }, [post, selectPlan, trackLockedUpgradeSuccess]);

  const handlePaymentCard = useCallback((nonce, proration) => {
    const cycle = billingCycle || "yearly";
    const payload = {
      a: window.btoa(selectPlan.toLowerCase()),
      b: window.btoa(cycle),
      g: window.btoa(source || "Editor"),
      h: window.btoa(currentTeamId),
      n: window.btoa(nonce),
      ce: window.btoa(proration),
      cc: "",
      cdc: "",
    };

    post(`${API_URL}${API.CBTS}`, payload, true).then((res) => {
      if (!res.error) {
        if (PLAN.LITE === current_plan) localStorage.removeItem("coupon");
        trackLockedUpgradeSuccess("card", selectPlan);
        setDisableLoading(true);
        setShowPaymentModal(false);
        setPaymentSuccessModal(true);
      } else {
        warn(res.errormsg || res.msg);
        setDisableLoading(true);
        setShowPaymentModal(false);
      }
    }).catch(() => {
      setDisableLoading(true);
      setShowPaymentModal(false);
      warn(content.SOMETHING_WENT_WRONG);
    });
  }, [billingCycle, currentTeamId, current_plan, post, selectPlan, source, trackLockedUpgradeSuccess, warn]);

  const handleCucCall = useCallback((url, nonce, change) => {
    post(url, {}).then((res) => {
      if (res.success) {
        handlePaymentCard(nonce, change);
      } else {
        warn(res.errormsg || res.msg);
        setShowPaymentModal(false);
        setDisableLoading(true);
      }
    }).catch(() => {
      setDisableLoading(true);
      setShowPaymentModal(false);
      warn(content.SOMETHING_WENT_WRONG);
    });
  }, [handlePaymentCard, post, warn]);

  const handlePaypalPayment = useCallback((cycle) => {
    post(`${API_URL}${API.BUY_SUBSCRIPTION}`, {
      a: window.btoa(selectPlan),
      b: window.btoa(cycle),
      g: window.btoa("Dashboard"),
      h: window.btoa(currentTeamId),
    }, true).then((res) => {
      setDisableLoading(true);
      if (!res.error && res.redirect_url) {
        trackLockedUpgradeSuccess("paypal", selectPlan);
        window.location.href = res.redirect_url;
      } else if (res.error) {
        warn(res.errormsg || res.msg);
      }
    }).catch(() => {
      setDisableLoading(true);
      warn(content.SOMETHING_WENT_WRONG);
    });
  }, [currentTeamId, post, selectPlan, trackLockedUpgradeSuccess, warn]);

  const handleRazorpayPayment = useCallback(async (selectedPlanValue, planCycleName) => {
    await loadScript("https://checkout.razorpay.com/v1/checkout.js");
    post(`${API_URL}${API.CREATE_RAZORPAY}`, {
      a: window.btoa(selectedPlanValue),
      b: window.btoa(planCycleName),
      g: window.btoa("Dashboard"),
      firstname: username,
    }, true).then((res) => {
      if (!res.error) {
        setDisableLoading(true);
        const rzp1 = new window.Razorpay({
          key: res.key_id,
          currency: "INR",
          name: "Vmaker AI",
          image: `${STATIC_PATH}vmakerai_logo.svg`,
          description: res.plan_name,
          subscription_id: res.subscription_id,
          callbaclk_url: res.url,
          redirect: false,
          handler: (response) => {
            setShowLoader(true);
            handleRazorpaySuccess(response, res.url);
          },
          modal: {
            escape: false,
            ondismiss() {
              setDisableLoading(true);
              setShowPaymentModal(false);
              setShowLoader(false);
              window.location.reload();
            },
          },
          prefill: { name: res.name, email: res.email },
          notes: { address: "Soumya Dey Corporate Office" },
          theme: { color: "#61dafb" },
        });
        rzp1.open();
      } else {
        setDisableLoading(true);
        setShowPaymentModal(false);
        warn(res.msg || res.errormsg);
      }
    }).catch(() => {
      setDisableLoading(true);
      setShowPaymentModal(false);
      warn(content.SOMETHING_WENT_WRONG);
    });
  }, [handleRazorpaySuccess, post, username, warn]);

  const handlePaymentBalance = useCallback((type, nonce) => {
    const cycle = billingCycle || "yearly";
    if (type === "paypal") {
      handlePaypalPayment(cycle);
    } else if (type === "razorpay") {
      handleRazorpayPayment(nonce.selectPlan, nonce.planCycleName);
    } else if (type === "changeCard" || type === "card") {
      const url = type === "card"
        ? `${API_URL}${API.PAYMENTS_PUC}`
        : `${API_URL}${API.PAYMENTS_CUC}`;
      handleCucCall(url, type === "card" ? "" : nonce, type === "card" ? 1 : 0);
    }
  }, [billingCycle, handleCucCall, handlePaypalPayment, handleRazorpayPayment]);

  const handlePlanChange = useCallback((value) => {
    getPlansPricingDetails(value);
  }, [getPlansPricingDetails]);

  const getPrice = (planKey) => {
    const pricing = planCost[planKey];
    if (!pricing) return null;
    const symbol = SYMBOL[countryCode] || SYMBOL.default;
    const isYearly = activeTab === 1;
    const monthlyAmount = isYearly ? pricing.yearly / 12 : pricing.monthly;
    const billedAmount = isYearly ? pricing.yearly : pricing.monthly;

    return {
      amount: `${symbol}${formatPriceByLocale(monthlyAmount)}`,
      billedAmount: `${symbol}${formatPriceByLocale(billedAmount)}`,
      billingNoteKey: isYearly
        ? content.SHORTS_PRICING_BILLED_YEARLY
        : content.SHORTS_PRICING_BILLED_MONTHLY,
    };
  };

  if (!isShortsPricingTrigger && !showPaymentModal && !showLoader && !paymentSuccessModal) return null;

  return (
    <>
      {isShortsPricingTrigger && (
        <Modal
          showModal={show}
          onClose={handleClose}
          fullScreen
          overlayBg={vmTheme[theme].shortsPricingOverlayBg}
          width="100%"
          height="100%"
          maxHeight="100%"
          smHeight="100%"
          overflow="auto"
          borderRadius="0"
          padding="0"
          closeIconPath="vm-close-new.svg"
          closeIconWidth="24px"
          closeIconHeight="24px"
          closeIconRight="20px"
          closeIconTop="20px"
          closeIconMobRight="12px"
          closeIconMobTop="12px"
          closeIconBg={vmTheme[theme].shortsPricingCloseIconBg}
          closeIconHoverBg={vmTheme[theme].shortsPricingCloseIconHoverBg}
        >
          <Page onClick={handleClose}>
            <ContentWrap onClick={handleContentWrapClick}>
              <Header onClick={(e) => e.stopPropagation()}>
                <Typography
                  content={content.SHORTS_PRICING_UPGRADE_TITLE}
                  font={font.bold_20_16}
                  color={vmTheme[theme].shortsPricingTextPrimary}
                  enableTrim={false}
                />
                <BillingToggle>
                  <BillingOption
                    type="button"
                    $active={activeTab === 0}
                    onClick={() => !(current_cycle === "yearly" || current_cycle === "monthly") && handleTabChange(0)}
                    disabled={current_cycle === "yearly" || current_cycle === "monthly"}
                    style={{ opacity: (current_cycle === "yearly" || current_cycle === "monthly") ? 0.5 : 1, cursor: (current_cycle === "yearly" || current_cycle === "monthly") ? "not-allowed" : "pointer" }}
                  >
                    <FormattedMessage id={content.SHORTS_PRICING_MONTHLY} />
                  </BillingOption>
                  <BillingOption
                    type="button"
                    $active={activeTab === 1}
                    onClick={() => handleTabChange(1)}
                  >
                    <FormattedMessage id={content.SHORTS_PRICING_YEARLY} />
                    {save > 0 && <DiscountLabel>{save}% off</DiscountLabel>}
                  </BillingOption>
                </BillingToggle>
              </Header>

              <PlanGrid onClick={handleContentWrapClick}>
                {SHORTS_PLANS.filter((planItem) => {
                  // Lite plan: show Starter, Team, Enterprise
                  if (current_plan === PLAN.LITE) {
                    return [PLAN.STARTER, PLAN.TEAM, PLAN.ENTERPRISE].includes(planItem.plan);
                  }
                  // Starter plan: if on monthly, show Starter (yearly), Team, Enterprise
                  if (current_plan === PLAN.STARTER) {
                    if (current_cycle === "monthly") {
                      return [PLAN.STARTER, PLAN.TEAM, PLAN.ENTERPRISE].includes(planItem.plan);
                    }
                    // If on yearly, show only Team and Enterprise
                    return [PLAN.TEAM, PLAN.ENTERPRISE].includes(planItem.plan);
                  }
                  // Team plan: show Enterprise only
                  if (current_plan === PLAN.TEAM) {
                    return planItem.plan === PLAN.ENTERPRISE;
                  }
                  return true;
                }).map((planItem) => {
                  const isTeamPlan = planItem.popular;
                  const price = planItem.plan === PLAN.ENTERPRISE
                    ? null
                    : getPrice(planItem.plan === PLAN.STARTER ? "starter" : "team");

                  return (
                    <PlanCard key={planItem.plan} $popular={isTeamPlan} onClick={(e) => e.stopPropagation()}>
                      {isTeamPlan && (
                        <PopularBadge>
                          <FormattedMessage id={content.SHORTS_PRICING_MOST_POPULAR} />
                        </PopularBadge>
                      )}
                      <PlanCardIntro>
                        <PlanTitleRow>
                          {planItem.icon && (
                            <PlanIcon src={`${STATIC_PATH}${planItem.icon}`} alt="" />
                          )}
                          <Typography
                            content={planItem.name}
                            font={font.bold_16}
                            color={vmTheme[theme].shortsPricingTextPrimary}
                            enableTrim={false}
                            display="block"
                          />
                        </PlanTitleRow>
                        <Typography
                          content={planItem.titleSubtext}
                          font={font.normalDefault_19}
                          color={vmTheme[theme].shortsPricingTextSecondary}
                          padding="8px 0 0"
                          display="block"
                          enableTrim={false}
                        />
                      </PlanCardIntro>
                      <PlanPriceSection>
                        <PriceRow>
                          {planItem.plan === PLAN.ENTERPRISE ? (
                            <Typography
                              content={planItem.priceLabel}
                              font={font.bold_28}
                              color={vmTheme[theme].shortsPricingTextPrimary}
                              enableTrim={false}
                            />
                          ) : (
                            <Typography
                              innerContent={price?.amount}
                              font={font.bold_28}
                              color={vmTheme[theme].shortsPricingTextPrimary}
                              enableTrim={false}
                            />
                          )}
                          <Typography
                            content={content.SHORTS_PRICING_PER_MO}
                            font={font.normal_13_22}
                            color={vmTheme[theme].shortsPricingTextSecondary}
                            enableTrim={false}
                            isVisible={planItem.plan !== PLAN.ENTERPRISE}
                          />
                        </PriceRow>
                        {planItem.plan === PLAN.ENTERPRISE ? (
                          <BilledAmountPlaceholder aria-hidden="true">&nbsp;</BilledAmountPlaceholder>
                        ) : (
                          price?.billedAmount && (
                            <Typography
                              innerContent={intl.formatMessage(
                                { id: price.billingNoteKey },
                                { amount: price.billedAmount },
                              )}
                              font={font.normalMicro12}
                              color={vmTheme[theme].shortsPricingOfferColor}
                              enableTrim={false}
                            />
                          )
                        )}
                        {planItem.creditsNote && (
                          <CreditsNoteRow>
                            <FormattedMessage id={planItem.creditsNote} />
                            {planItem.creditsInfoTooltip && (
                              <PricingInfoIcon
                                tooltipId={`shorts-pricing-credits-tt-${planItem.plan}`}
                                contentKey={planItem.creditsInfoTooltip}
                              />
                            )}
                          </CreditsNoteRow>
                        )}
                      </PlanPriceSection>
                      <PlanButtonRow>
                        <PlanButton
                          type="button"
                          $plan={planItem.plan}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlanClick(e, planItem.plan, activeTab)
                          }
                          }
                        >
                          <FormattedMessage id={planItem.cta} />
                        </PlanButton>
                      </PlanButtonRow>
                      <PlanCardFeatures>
                        {planItem.featuresHeader && (
                          <FeaturesHeader>
                            <FormattedMessage id={planItem.featuresHeader} />
                          </FeaturesHeader>
                        )}
                        <FeatureList>
                          {planItem.features.map((feature) => {
                            const featureKey = getFeatureKey(feature);
                            const infoType = getFeatureInfoType(feature);
                            const comingSoon = getFeatureComingSoon(feature);
                            return (
                              <li key={featureKey}>
                                <FeatureLabel>
                                  <FormattedMessage id={featureKey} />
                                  {comingSoon && (
                                    <>
                                      {" "}
                                      <ComingSoonText>
                                        (
                                        <FormattedMessage id={content.COMING_SOON} />
                                        )
                                      </ComingSoonText>
                                    </>
                                  )}
                                  {infoType === "urlPlatforms" && <UrlPlatformsInfoIcon />}
                                </FeatureLabel>
                              </li>
                            );
                          })}
                        </FeatureList>
                      </PlanCardFeatures>
                    </PlanCard>
                  );
                })}
              </PlanGrid>
            </ContentWrap>
          </Page>
        </Modal>
      )}

      {showPaymentModal && (
        <PaymentModal
          isShow={showPaymentModal}
          tenureEnable={true}
          planDetails={paymentDetails}
          billingCycle={billingCycle}
          title={content.SHORTS_PRICING_UPGRADE_TITLE}
          onAction={handlePaymentBalance}
          onClose={handleOnClose}
          teamCount={member_count || 1}
          planChange={handlePlanChange}
          monthlyAmount={monthlyAmount}
          yearlyAmount={yearlyAmount}
          selectPlan={selectPlan}
          refundAmount={refundAmount}
          getBillingCycle={(value) => setBillingCycle(value)}
          loadingDisable={disableLoading}
          paymentMode={paymentMode}
          defaultCycle={paymentCycle}
          regularPrice={regularPrice}
          countryCode={countryCode}
          countryName={countryName}
          changePaymentCurrency={handlePaymentCurrency}
          paymentCurrency={paymentCurrency}
          cummulative={cummulativePrice}
          totalAmount={totalPayableAmount}
          countryPin={countryCode}
          zIndex={10000}
        />
      )}
      {paymentSuccessModal && (
        <PaymentSuccess
          message={intl.formatMessage(
            { id: content.SHORTS_PRICING_UPGRADE_SUCCESS },
            { plan: selectPlan },
          )}
        />
      )}
      {showLoader && (
        <BackdropLoader content={intl.formatMessage({ id: content.PAYMENT_PROCESSING })} />
      )}
    </>
  );
};

export default ShortsPricingModal;
